import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';

import { AuthService } from '@services/http/auth.service';
import { GrowthBookService } from '@app/shared/modules/growthbook';
import { StateService } from '@services/Logic/state.service';
import { StorageService } from '@services/Logic/storage.service';
import { UserService } from '@services/http/user.service';
import { EventService } from '@app/services/Logic/Event.service';
import { MiscService } from '@app/services/http/misc.service';

import { UserStore } from '@app/shared/store/user.store.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  public showHeaderResult: boolean;
  currentUrl: string;
  hideHeaderFooterRef: Subscription;
  showNewMessagesHeaderComponentRef: Subscription;
  innerWidth: any = 0; // keep track of the window width
  isHeaderFooterHidden = false; // used to show/hide header/footer on mobile
  newMessagesCount: number; // used to show new messages in header/footer
  showFooterNavigation: boolean = false;
  isLoading = false;
  isLoadingExtended = false;
  isAuth = false;
  disabled = false;
  renderCount = 0;
  showExtenedLoader = false;

  constructor(
    public authService: AuthService,
    private eventService: EventService,
    private miscService: MiscService,
    private router: Router,
    private stateService: StateService,
    public storage: StorageService,
    private userService: UserService,
    private growthbook: GrowthBookService,
    private userStore: UserStore,
  ) {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const container = document.getElementsByClassName('container');
      if (container.length > 0) {
        container[0].scroll({ top: 0 });
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.innerWidth = window.innerWidth;
    this.showHeader();
    this.showFooter();
  }

  ngOnInit() {
    this.newMessagesCount = 0;
    this.innerWidth = window.innerWidth;
    this.listenToAuthStatus();
    this.listenToHideFooter();
    this.listenToNewMessages();
    this.listenToRoutes();
    this.handleLoadingEvent();
    this.handleExtendedLoadingEvent();
    this.stateService.isAuthenticatedStateSource
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.isAuth = res;
      });
    if (!this.storage.showCompleteProfile) {
      this.storage.showCompleteProfile = this.storage.initStorageCookie();
    } else {
      this.storage.showCompleteProfile = this.storage.checkForCompleteProfile();
    }
    this.storage.promptSetupProfileViewed = false;
    this.showFooter();
  }

  ngOnDestroy() {
    this.hideHeaderFooterRef.unsubscribe();
    this.showNewMessagesHeaderComponentRef.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private listenToRoutes(): void {
    this.router.events.forEach((item) => {
      if (item instanceof NavigationEnd) {
        this.currentUrl = item.url;
        this.eventService.customNewPageEvent(this.storage?.user, item.url);
        this.showFooter();
        this.showHeader();
      }
    });
  }

  private listenToHideFooter(): void {
    this.hideHeaderFooterRef =
      this.stateService.hideHeaderFooterComponentSource.subscribe(() => {
        this.showFooter();
      });
  }

  private listenToNewMessages(): void {
    this.showNewMessagesHeaderComponentRef =
      this.stateService.showNewMessagesHeaderComponentSource.subscribe(
        (res: number) => {
          this.newMessagesCount = res;
        },
      );
  }

  private listenToAuthStatus() {
    this.authService.authStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (isAuth) => {
        this.isAuth = isAuth;
        if (this.isAuth) {
          const lastFetchTimestamp = this.storage.userProfileLastFetch;
          const currentTime = new Date().getTime();
          const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
          const shouldRefetch: boolean =
            !lastFetchTimestamp ||
            currentTime - lastFetchTimestamp > twentyFourHoursInMs;
          if (shouldRefetch || !this.storage.user) {
            this.storage.hasCPR = await this.userService.getUserHasCPR();
            this.userService
              .getUserProfile()
              .pipe(takeUntil(this.destroy$))
              .subscribe((res) => {
                this.userStore.setUser(res);
                this.authService.getUserSettingsdata();
                this.miscService.recordSession().subscribe();
                this.storage.userProfileLastFetch = currentTime;
              });
          }
        }
        this.updateGrowthBookAttributes();
      });
  }

  private updateGrowthBookAttributes(): void {
    this.growthbook.updateAttributes({
      user_id: this.storage?.user?.id,
      gbuuid: this.storage.getGrowthbookUUID(),
      url: window.location.href,
    });
  }

  handleLoadingEvent(): void {
    this.stateService.toggleWaitingComponentSource
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.isLoading = res;
      });
  }

  handleExtendedLoadingEvent(): void {
    this.stateService.toggleExtendedWaitingComponentSource
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ status, title, steps, hasError }) => {
        this.isLoadingExtended = status;
        if (status) {
          this.showExtenedLoader = true;
        }
        if (hasError) {
          this.showExtenedLoader = false;
        }
      });
  }

  showHeader(): boolean {
    const url: string = this.router.url.split('?')[0];
    const hiddenRoutes = ['/create', '/reset'];
    return (
      hiddenRoutes.indexOf(url) === -1 &&
      (this.innerWidth > 810 || !this.isHeaderFooterHidden)
    );
  }

  private isHiddenRoute(url: URL): boolean {
    const pathName = url.pathname;
    // Check if the pathname starts with '/opret' and has any query parameters
    return pathName.startsWith('/opret') && url.search.length > 0;
  }

  public showFooter(): void {
    const url = new URL(this.currentUrl, window.location.origin);
    this.showFooterNavigation =
      this.innerWidth <= 810 &&
      !this.isHiddenRoute(url) &&
      this.authService.isAuthenticated() &&
      !this.isHeaderFooterHidden &&
      !url.pathname.startsWith('/opret');
  }

  showHideExtendedLoader(): void {
    this.showExtenedLoader = false;
  }
}
